import { supabase } from '@/lib/supabase';
import type { ApiResponse } from '@/types';

export interface UserData {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
}

export interface UpdateUserDataResponse extends ApiResponse<UserData> {}

/**
 * 사용자 현재 데이터 가져오기
 * 입력: userId (string)
 * 출력: users 테이블의 사용자 정보
 */
export async function getCurrentUserData(userId: string): Promise<ApiResponse<UserData>> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, phone, name, role')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Failed to fetch user data:', error);
      return {
        success: false,
        error: '사용자 정보를 불러오는데 실패했습니다.'
      };
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error fetching user data:', error);
    return {
      success: false,
      error: '사용자 정보를 불러오는 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 사용자 이메일 업데이트
 * 입력: userId (string), newEmail (string)
 * 처리: users 테이블의 email 필드 업데이트, updated_at 자동 갱신
 * 출력: 업데이트된 사용자 정보
 */
export async function updateUserEmail(userId: string, newEmail: string): Promise<UpdateUserDataResponse> {
  try {
    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      return {
        success: false,
        error: '올바른 이메일 형식을 입력해주세요.'
      };
    }

    // 이메일 중복 확인
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('email', newEmail)
      .neq('id', userId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Email check error:', checkError);
      return {
        success: false,
        error: '이메일 중복 확인 중 오류가 발생했습니다.'
      };
    }

    if (existingUser) {
      return {
        success: false,
        error: '이미 사용 중인 이메일입니다.'
      };
    }

    // users 테이블 업데이트
    const { data, error } = await supabase
      .from('users')
      .update({ 
        email: newEmail,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select('id, email, phone, name, role')
      .single();

    if (error) {
      console.error('Failed to update email:', error);
      return {
        success: false,
        error: '이메일 변경에 실패했습니다.'
      };
    }

    // Supabase Auth 이메일도 업데이트
    try {
      const { error: authError } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (authError) {
        console.error('Auth email update error:', authError);
        // 데이터베이스는 이미 업데이트되었으므로 경고만 표시
        console.warn('Database updated but auth email update failed');
      }
    } catch (authError) {
      console.warn('Auth email update failed:', authError);
    }

    return {
      success: true,
      data,
      message: '이메일이 성공적으로 변경되었습니다.'
    };
  } catch (error) {
    console.error('Error updating email:', error);
    return {
      success: false,
      error: '이메일 변경 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 사용자 비밀번호 업데이트
 * 입력: newPassword (string)
 * 처리: Supabase Auth updateUser() 호출
 * 출력: 성공/실패 응답
 */
export async function updateUserPassword(newPassword: string): Promise<ApiResponse<null>> {
  try {
    // 비밀번호 강도 검증 (8자 이상)
    if (newPassword.length < 8) {
      return {
        success: false,
        error: '비밀번호는 8자 이상이어야 합니다.'
      };
    }

    // Supabase Auth를 통한 비밀번호 업데이트
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error('Failed to update password:', error);
      return {
        success: false,
        error: '비밀번호 변경에 실패했습니다.'
      };
    }

    return {
      success: true,
      message: '비밀번호가 성공적으로 변경되었습니다.'
    };
  } catch (error) {
    console.error('Error updating password:', error);
    return {
      success: false,
      error: '비밀번호 변경 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 사용자 전화번호 업데이트 (인증 불필요)
 * 입력: userId (string), newPhone (string)
 * 처리: users 테이블의 phone 필드 업데이트, updated_at 자동 갱신
 * 출력: 업데이트된 사용자 정보
 */
export async function updateUserPhone(userId: string, newPhone: string): Promise<UpdateUserDataResponse> {
  try {
    // 전화번호 형식 검증 (숫자만, 10-11자리)
    const phoneRegex = /^[0-9]{10,11}$/;
    if (!phoneRegex.test(newPhone)) {
      return {
        success: false,
        error: '올바른 전화번호 형식을 입력해주세요. (숫자만 10-11자리)'
      };
    }

    // users 테이블 업데이트
    const { data, error } = await supabase
      .from('users')
      .update({ 
        phone: newPhone,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select('id, email, phone, name, role')
      .single();

    if (error) {
      console.error('Failed to update phone:', error);
      return {
        success: false,
        error: '전화번호 변경에 실패했습니다.'
      };
    }

    return {
      success: true,
      data,
      message: '전화번호가 성공적으로 변경되었습니다.'
    };
  } catch (error) {
    console.error('Error updating phone:', error);
    return {
      success: false,
      error: '전화번호 변경 중 오류가 발생했습니다.'
    };
  }
}

/**
 * OTP 발송을 위한 헬퍼 함수
 * 입력: email (string), purpose ('change_email' | 'reset_password'), name? (string)
 * 처리: send-otp Edge Function 호출
 * 출력: OTP 발송 결과
 */
export async function sendOTPForSettings(
  email: string, 
  purpose: 'change_email' | 'reset_password',
  name?: string
): Promise<ApiResponse<{ remainingAttempts?: number; nextAllowedAt?: string }>> {
  try {
    const { data, error } = await supabase.functions.invoke('send-otp', {
      body: { 
        email,
        purpose,
        name
      }
    });

    if (error) {
      console.error('OTP send error:', error);

       // 429 등 함수 에러의 상세 메시지 추출
       let parsedBody: any = null;
       try {
         parsedBody = await (error as any)?.context?.response?.json();
       } catch {
         // ignore
       }
       const status = (error as any)?.context?.response?.status;
       const friendlyMessage =
         parsedBody?.error ||
         (status === 429
           ? '1시간 내 최대 3회까지만 인증번호를 요청할 수 있습니다. 잠시 후 다시 시도해주세요.'
           : '인증번호 발송에 실패했습니다.');

      return {
        success: false,
        error: friendlyMessage,
        data: {
          remainingAttempts: parsedBody?.remainingAttempts,
          nextAllowedAt: parsedBody?.nextAllowedAt
        }
      };
    }

    if (data?.success) {
      return {
        success: true,
        data: {
          remainingAttempts: data.remainingAttempts,
          nextAllowedAt: data.nextAllowedAt
        },
        message: data.message
      };
    } else {
      return {
        success: false,
        error: data?.error || '인증번호 발송에 실패했습니다.',
        data: {
          remainingAttempts: data?.remainingAttempts,
          nextAllowedAt: data?.nextAllowedAt
        }
      };
    }
  } catch (error) {
    console.error('Error sending OTP:', error);
    return {
      success: false,
      error: '인증번호 발송 중 오류가 발생했습니다.'
    };
  }
}

/**
 * OTP 검증을 위한 헬퍼 함수
 * 입력: email (string), otp (string), purpose ('change_email' | 'reset_password')
 * 처리: verify-otp Edge Function 호출
 * 출력: 검증 결과
 */
export async function verifyOTPForSettings(
  email: string, 
  otp: string,
  purpose: 'change_email' | 'reset_password'
): Promise<ApiResponse<{ verificationToken?: string; remainingAttempts?: number }>> {
  try {
    const { data, error } = await supabase.functions.invoke('verify-otp', {
      body: { 
        email,
        otp,
        purpose
      }
    });

    if (error) {
      console.error('OTP verify error:', error);

      // 함수 오류(예: 400, 429) 상세 메시지 추출
      let parsedBody: any = null;
      try {
        parsedBody = await (error as any)?.context?.response?.json();
      } catch {
        // ignore
      }
      const status = (error as any)?.context?.response?.status;
      const friendlyMessage =
        parsedBody?.error ||
        (status === 429
          ? '인증 시도 횟수를 초과했습니다. 새로운 인증번호를 요청해주세요.'
          : '인증번호 확인에 실패했습니다.');

      return {
        success: false,
        error: friendlyMessage,
        data: {
          remainingAttempts: parsedBody?.remainingAttempts
        }
      };
    }

    if (data?.success) {
      return {
        success: true,
        data: {
          verificationToken: data.verificationToken,
          remainingAttempts: data.remainingAttempts
        },
        message: '인증이 완료되었습니다.'
      };
    } else {
      return {
        success: false,
        error: data?.error || '인증번호가 올바르지 않습니다.',
        data: {
          remainingAttempts: data?.remainingAttempts
        }
      };
    }
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return {
      success: false,
      error: '인증번호 확인 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 비밀번호 재설정을 위한 OTP 발송
 * 입력: email (string), name? (string)
 * 처리: send-otp Edge Function 호출
 * 출력: OTP 발송 결과
 */
export async function sendOTPForPasswordReset(
  email: string, 
  name?: string
): Promise<ApiResponse<{ remainingAttempts?: number; nextAllowedAt?: string }>> {
  return sendOTPForSettings(email, 'reset_password', name);
}

/**
 * 비밀번호 재설정을 위한 OTP 검증
 * 입력: email (string), otp (string)
 * 처리: verify-otp Edge Function 호출
 * 출력: 검증 결과
 */
export async function verifyOTPForPasswordReset(
  email: string, 
  otp: string
): Promise<ApiResponse<{ verificationToken?: string; remainingAttempts?: number }>> {
  return verifyOTPForSettings(email, otp, 'reset_password');
}

/**
 * OTP 검증 후 비밀번호 재설정
 * 입력: email (string), newPassword (string), verificationToken? (string)
 * 처리: 사용자 확인 후 비밀번호 업데이트
 * 출력: 비밀번호 재설정 결과
 */
export async function resetPasswordWithOTP(
  email: string, 
  newPassword: string,
  otp: string
): Promise<ApiResponse<null>> {
  try {
    // 비밀번호 강도 검증 (8자 이상)
    if (newPassword.length < 8) {
      return {
        success: false,
        error: '비밀번호는 8자 이상이어야 합니다.'
      };
    }

    if (!otp || otp.length !== 6) {
      return {
        success: false,
        error: '인증번호가 올바르지 않습니다.'
      };
    }

    const { data, error } = await supabase.functions.invoke('reset-password-with-otp', {
      body: { email, otp, newPassword }
    });

    if (error) {
      console.error('Failed to reset password via function:', error);
      return {
        success: false,
        error: '비밀번호 재설정에 실패했습니다.'
      };
    }

    if (!data?.success) {
      return {
        success: false,
        error: data?.error || '비밀번호 재설정에 실패했습니다.'
      };
    }

    return {
      success: true,
      message: data.message || '비밀번호가 성공적으로 재설정되었습니다.'
    };
  } catch (error) {
    console.error('Error resetting password:', error);
    return {
      success: false,
      error: '비밀번호 재설정 중 오류가 발생했습니다.'
    };
  }
}
